import React, { useState, useEffect, useCallback } from "react";
import MarkdownIt from "markdown-it";
import { useParams } from "wouter";
import { Button } from "../../../../components/src/Button";
import { Alert, AlertVariant } from "../../../../components/src/Alert";
import MdEditor from "react-markdown-editor-lite";
import { gql, useMutation, useQuery } from "urql";
import { useQueryState } from "../../../../components/src/use-query-state";
import * as Tabs from "@radix-ui/react-tabs";
import { DateForm } from "../../../../components/src/dateForm";

import { useAlertTimeOut, usePrevious } from "../../../../components/src/common/Utils";
import { serializeSwedenDate } from "../../../../components/src/dateFormater";
import { cloneDeep } from "lodash";
import { ResponsiveTabsList } from "../../../../components/src/ResponsiveTabsList";
import { PmSelectPrompt } from "./PmSelectPrompt";

const GET_DATA = gql`
    query getData($clientId: GraphQLObjectId!, $date: GraphQLDateString) {
        portfolioManagerComment: reports(clientId: $clientId, type: "PortfolioManagerComment", date: $date, lastOnly: false) {
            _id
            type
            data
            name
            clientId
        }
    }
`;

export const UPDATE_PORTFOLIO_MANAGER_COMMENT = gql`
    mutation updateReport($input: UpdateReportInput!) {
        update: updateReport(input: $input) {
            _id
        }
    }
`;

export const CREATE_PORTFOLIO_MANAGER_COMMENT = gql`
    mutation createReport($input: CreateReportInput!) {
        create: createReport(input: $input) {
            _id
        }
    }
`;

export interface PortfolioManagerCommentProps {
    locale: string;
    refetch?: () => void;
    date: string;
}

const tabs = {
    sv: "sv",
    en: "en"
};

export function PortfolioManagerComment({ locale = "sv", refetch, date }: PortfolioManagerCommentProps): React.ReactElement {
    const { id }: any = useParams();
    const previousDate = usePrevious(date);

    const clientId = id && id !== "new" ? id : "000000000000000000000000";

    const [comment, setComment] = useState("");
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });

    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    const [{ fetching: loading, error, data }, refetchData] = useQuery({
        query: GET_DATA,
        variables: { clientId: clientId, date: date },
        requestPolicy: "network-only"
    });

    useAlertTimeOut(alert, setAlert, 5);

    const [stateCreate, createMutation] = useMutation(CREATE_PORTFOLIO_MANAGER_COMMENT);
    const [stateUpdate, updateMutation] = useMutation(UPDATE_PORTFOLIO_MANAGER_COMMENT);

    useEffect(() => {
        if (
            previousDate !== date ||
            (stateCreate && stateCreate.data && !stateCreate.data.error) ||
            (stateUpdate && stateUpdate.data && !stateUpdate.data.error)
        ) {
            refetchData();
        }
    }, [stateCreate, stateUpdate, date, previousDate, refetchData]);

    useEffect(() => {
        if (data && data.portfolioManagerComment.length > 0) {
            setComment(data.portfolioManagerComment[0].data[locale]);
        } else {
            setComment("");
        }
    }, [data, locale, clientId, date]);

    if (typeof clientId === "undefined" || clientId === "all") {
        return <PmSelectPrompt message="Select a client" />;
    }

    const mdParser = new MarkdownIt();

    if (loading)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    const isCreateMode = data && data.portfolioManagerComment.length === 0;

    return (
        <div className="page">
            <MdEditor
                style={{ height: "500px" }}
                value={comment}
                renderHTML={(text) => mdParser.render(text)}
                onChange={({ text }) => {
                    setComment(text);
                }}
            />

            <div className="mt-4">
                <Button
                    id="button"
                    size="sm"
                    variant="primary"
                    onClick={async () => {
                        if (isCreateMode) {
                            const input = {
                                clientId: clientId,
                                type: "PortfolioManagerComment",
                                date: date,
                                data: { sv: "", en: "" },
                                name: "Portfolio Manager Comment"
                            };
                            input.data[locale] = comment;

                            await createMutation({ input })
                                .then((result) => {
                                    if (result.error) {
                                        setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                    } else {
                                        setAlert({
                                            color: "success",
                                            visible: true,
                                            message: `New portfolio manager comment'${result.data.create._id}' created successfully!`
                                        });
                                    }
                                })
                                .catch((error) => {
                                    console.error(error.toString());
                                })
                                .finally(() => {
                                    refetchData();
                                    if (refetch) {
                                        refetch();
                                    }
                                });
                        } else {
                            const input = cloneDeep(data.portfolioManagerComment[0]);
                            delete input.__typename;
                            input.date = date;
                            input.data[locale] = comment;

                            await updateMutation({ input: input })
                                .then((result) => {
                                    if (result.error) {
                                        setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                    } else {
                                        setAlert({
                                            color: "success",
                                            visible: true,
                                            message: `The portfolio manager comment with id '${result.data.update._id}' updated successfully!`
                                        });
                                    }
                                })
                                .catch((error) => {
                                    console.error(error.toString());
                                })
                                .finally(() => {
                                    refetchData();
                                    if (refetch) {
                                        refetch();
                                    }
                                });
                        }
                    }}
                >
                    {isCreateMode ? "Create" : "Update"}
                </Button>
            </div>
            <div className="flex flex-wrap -mx-2 mt-4">
                <div className="w-full md:w-2/3 lg:w-1/2 px-2 mb-4">
                    {alert.visible ? (
                        <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                            {alert.message}
                        </Alert>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export const PortfolioManagerCommentTabs = (): React.ReactElement => {
    const [date, _setDate] = useQueryState("date", serializeSwedenDate(new Date()));
    const [lang, _setLang] = useQueryState("lang", tabs.sv);

    if (!(lang in tabs)) {
        _setLang(tabs.sv);
    }

    const onTabChange = useCallback(
        (tabId) => {
            _setLang(tabId);
        },
        [_setLang]
    );

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-1/3 lg:w-1/3 px-2 mb-4">
                    <DateForm defaultDateString={date} dateName={"date"}></DateForm>
                </div>
                <div className="bg-gray-100 rounded-lg p-4 shadow-sm w-full px-2 mb-4">
                    <Tabs.Root value={lang} onValueChange={onTabChange} className="w-full">
                        <ResponsiveTabsList value={lang} onValueChange={onTabChange}>
                            <Tabs.Trigger
                                value="sv"
                                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                            >
                                sv
                            </Tabs.Trigger>
                            <Tabs.Trigger
                                value="en"
                                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                            >
                                en
                            </Tabs.Trigger>
                        </ResponsiveTabsList>
                        <Tabs.Content value="sv" className="mt-4">
                            <PortfolioManagerComment locale={"sv"} date={date} />
                        </Tabs.Content>
                        <Tabs.Content value="en" className="mt-4">
                            <PortfolioManagerComment locale={"en"} date={date} />
                        </Tabs.Content>
                    </Tabs.Root>
                </div>
            </div>
        </div>
    );
};
